// frontend/src/components/reputation/GeogridKeywordsModal.jsx
// Version: 2.0 (reputation dictionary i18n)
// Description: Модальное окно управления ключевыми словами геосетки для офиса.
// Прозрачность бюджета ($0.10/замер/запрос), ограничение 1..10 запросов, честный аудит.

import React, { useState, useEffect, useCallback } from 'react';
import {
  X,
  Plus,
  Trash2,
  DollarSign,
  AlertCircle,
  CheckCircle2,
  Sliders,
  Info,
  Calendar
} from 'lucide-react';
import reputationService from '../../services/reputationService';
import { formatEdtDateTime } from './OfficeVisibilityBlock';
import { useUiLanguage } from '../../context/UiLanguageContext';
import { perevesti } from '../../i18n/reputation';

export const GeogridKeywordsModal = ({
  isOpen,
  onClose,
  kluch,
  officeName = '',
  onSaved = () => {},
}) => {
  const { lang: rawLang } = useUiLanguage();
  const lang = rawLang || 'ru';
  const t = useCallback((k, vars) => perevesti(lang, k, vars), [lang]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const [keywords, setKeywords] = useState([]);
  const [newKeywordInput, setNewKeywordInput] = useState('');
  const [source, setSource] = useState('config');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [maxKeywords, setMaxKeywords] = useState(null);
  const [abortStatus, setAbortStatus] = useState(null);

  // Загрузка настроек при открытии модального окна
  useEffect(() => {
    if (!isOpen || !kluch) return;

    let isMounted = true;
    setLoading(true);
    setError(null);
    setSuccessMsg(null);
    setNewKeywordInput('');

    reputationService
      .getGeogridSettings(kluch)
      .then((res) => {
        if (!isMounted) return;
        setSource(res.source || 'config');
        setUpdatedAt(res.updated_at || null);
        setUpdatedBy(res.updated_by || null);
        setMaxKeywords(typeof res.max_keywords === 'number' ? res.max_keywords : null);
        setAbortStatus(res.abort_status || null);

        if (Array.isArray(res.keywords) && res.keywords.length > 0) {
          setKeywords(res.keywords);
        } else {
          setKeywords([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!isMounted) return;
        console.warn(`[GeogridKeywordsModal] Error loading settings for ${kluch}:`, err);
        setError(err.response?.data?.detail || err.message || t('rep.modal.geogrid.err_load'));
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, kluch, t]);

  if (!isOpen) return null;

  const count = keywords.length;
  const costPerRunUsd = (count * 0.10).toFixed(2);
  const costPerMonthUsd = (count * 0.40).toFixed(2);

  const handleAddKeyword = (e) => {
    if (e) e.preventDefault();
    const trimmed = newKeywordInput.trim();
    if (!trimmed) return;

    if (maxKeywords === null) {
      setError(t('rep.modal.geogrid.err_limit_not_reported'));
      return;
    }
    if (trimmed.length < 2) {
      setError(t('rep.modal.geogrid.err_min_chars'));
      return;
    }
    if (trimmed.length > 200) {
      setError(t('rep.modal.geogrid.err_max_chars'));
      return;
    }
    if (count >= maxKeywords) {
      setError(t('rep.modal.geogrid.err_max_queries', { max: maxKeywords }));
      return;
    }
    const lower = trimmed.toLowerCase();
    if (keywords.some((k) => k.toLowerCase() === lower)) {
      setError(t('rep.modal.geogrid.err_duplicate'));
      return;
    }

    setKeywords([...keywords, trimmed]);
    setNewKeywordInput('');
    setError(null);
  };

  const handleRemoveKeyword = (indexToRemove) => {
    if (keywords.length <= 1) {
      setError(t('rep.modal.geogrid.err_at_least_one'));
      return;
    }
    setKeywords(keywords.filter((_, idx) => idx !== indexToRemove));
    setError(null);
  };

  const handleSave = async () => {
    if (maxKeywords === null) {
      setError(t('rep.modal.geogrid.err_save_blocked'));
      return;
    }
    if (keywords.length === 0) {
      setError(t('rep.modal.geogrid.err_add_before_save'));
      return;
    }
    if (keywords.length > maxKeywords) {
      setError(t('rep.modal.geogrid.err_max_allowed', { max: maxKeywords }));
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMsg(null);

    try {
      const res = await reputationService.saveGeogridSettings(kluch, keywords);
      setSource(res.source || 'OS');
      setUpdatedAt(res.updated_at || new Date().toISOString());
      setUpdatedBy(res.updated_by || null);
      setAbortStatus(null);
      setSuccessMsg(t('rep.modal.geogrid.success_saved'));
      onSaved(res.keywords);
    } catch (err) {
      console.error('[GeogridKeywordsModal] Error saving settings:', err);
      setError(err.response?.data?.detail || err.message || t('rep.modal.geogrid.err_save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="geogrid-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-in fade-in duration-200"
    >
      <div className="bg-white rounded-xl shadow-2xl border border-[#E5E1D6] max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden text-xs">
        {/* Заголовок */}
        <div className="flex items-center justify-between p-4 border-b border-[#E5E1D6] bg-[#FAF9F5]">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-md bg-[#20231F] text-white">
              <Sliders className="w-4 h-4 text-[#C8A653]" />
            </div>
            <div>
              <h2 id="geogrid-modal-title" className="text-sm font-bold text-[#20231F]">
                {t('rep.modal.geogrid.title')}
              </h2>
              <div className="text-[11px] text-[#687077]">
                {officeName
                  ? t('rep.modal.geogrid.branch', { name: officeName })
                  : t('rep.modal.geogrid.key', { kluch })}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('rep.modal.geogrid.close')}
            className="p-1 rounded-md text-[#687077] hover:bg-[#E5E1D6] hover:text-[#20231F] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Тело модального окна */}
        <div className="p-4 overflow-y-auto space-y-4 flex-1">
          {loading ? (
            <div className="py-8 text-center text-[#687077] animate-pulse">
              {t('rep.modal.geogrid.loading')}
            </div>
          ) : (
            <>
              {/* Источник настроек */}
              <div className="flex items-center justify-between p-2.5 rounded-lg bg-[#FAF9F5] border border-[#E5E1D6]">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-[#20231F]">
                    {t('rep.modal.geogrid.source_label')}
                  </span>
                  {source === 'OS' ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-[#E6F6EE] text-[#0B8B5E] border border-[#B7E5D0]">
                      <CheckCircle2 className="w-3 h-3" />
                      {t('rep.modal.geogrid.source_os')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-[#F1EEE4] text-[#687077] border border-[#E5E1D6]">
                      <Info className="w-3 h-3 text-[#C8A653]" />
                      {t('rep.modal.geogrid.source_c2')}
                    </span>
                  )}
                </div>
                {updatedAt && (
                  <span
                    className="text-[10px] text-[#687077]"
                    title={updatedBy ? t('rep.modal.geogrid.author', { user: updatedBy }) : ''}
                  >
                    {formatEdtDateTime(updatedAt, lang)}
                  </span>
                )}
              </div>

              {/* Калькулятор прозрачности затрат */}
              <div className="p-3 rounded-lg bg-[#F8F9FA] border border-[#E5E1D6] space-y-2">
                <div className="flex items-center justify-between font-semibold text-[#20231F]">
                  <div className="flex items-center gap-1.5">
                    <DollarSign className="w-3.5 h-3.5 text-[#0B8B5E]" />
                    <span>{t('rep.modal.geogrid.budget_title')}</span>
                  </div>
                  <span className="text-[11px] font-mono font-bold text-[#20231F]">
                    {maxKeywords !== null
                      ? t('rep.modal.geogrid.queries_count', { count, max: maxKeywords })
                      : t('rep.modal.geogrid.limit_not_reported')}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 pt-1 text-[11px]">
                  <div className="p-2 rounded bg-white border border-[#E5E1D6]">
                    <div className="text-[10px] text-[#687077] uppercase font-bold">
                      {t('rep.modal.geogrid.single_scan')}
                    </div>
                    <div className="text-sm font-bold text-[#20231F] mt-0.5">
                      ≈ ${costPerRunUsd}
                    </div>
                    <div className="text-[10px] text-[#687077]">
                      {t('rep.modal.geogrid.points_calc', { points: count * 49 })}
                    </div>
                  </div>
                  <div className="p-2 rounded bg-white border border-[#E5E1D6]">
                    <div className="text-[10px] text-[#687077] uppercase font-bold">
                      {t('rep.modal.geogrid.per_month')}
                    </div>
                    <div className="text-sm font-bold text-[#0B8B5E] mt-0.5">
                      ≈ ${costPerMonthUsd}
                    </div>
                    <div className="text-[10px] text-[#687077]">
                      {t('rep.modal.geogrid.weekly_scan')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Уведомление об отмене замера роботом C2 (если замер сорвался) */}
              {abortStatus && (
                <div className="p-2.5 rounded-lg bg-[#FFF5F5] border border-[#FED7D7] text-[#C53030] text-[11px] flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-[#E53E3E]" />
                  <div className="flex-1">
                    <div className="font-bold">
                      {t('rep.modal.geogrid.abort_title', { reason: abortStatus.reason })}
                    </div>
                    <div className="text-[10px] text-[#742A2A] mt-0.5">
                      {t('rep.modal.geogrid.abort_desc', {
                        planned: abortStatus.planned_requests || '—',
                        limit: abortStatus.budget_limit || '250',
                        max: maxKeywords || 'the limit',
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Сообщения об ошибках и успехе */}
              {error && (
                <div className="p-2.5 rounded-lg bg-[#FFF5F5] border border-[#FED7D7] text-[#C53030] flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              {successMsg && (
                <div className="p-2.5 rounded-lg bg-[#E6F6EE] border border-[#B7E5D0] text-[#0B8B5E] flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  <span>{successMsg}</span>
                </div>
              )}

              {/* Список текущих запросов */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-[11px] font-bold text-[#20231F]">
                  <span>{t('rep.modal.geogrid.query_list_title')}</span>
                  <span className="text-[10px] font-normal text-[#687077]">
                    {maxKeywords !== null
                      ? t('rep.modal.geogrid.min_max', { max: maxKeywords })
                      : t('rep.modal.geogrid.limit_not_reported')}
                  </span>
                </div>

                {keywords.length === 0 ? (
                  <div className="p-3 text-center rounded-lg bg-white border border-dashed border-[#E5E1D6] text-[#687077]">
                    {source === 'config'
                      ? t('rep.modal.geogrid.unknown_list')
                      : t('rep.modal.geogrid.empty_list')}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {keywords.map((kw, idx) => (
                      <div
                        key={`${kw}-${idx}`}
                        className="flex items-center justify-between p-2 rounded-lg bg-white border border-[#E5E1D6] hover:border-[#C8A653] transition-colors"
                      >
                        <div className="flex items-center gap-2 min-w-0 pr-2">
                          <span className="font-mono text-[10px] font-bold text-[#8C7A5B] w-4 shrink-0">
                            {idx + 1}.
                          </span>
                          <span className="text-xs font-medium text-[#20231F] truncate" title={kw}>
                            {kw}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveKeyword(idx)}
                          aria-label={t('rep.modal.geogrid.delete_kw', { kw })}
                          className="p-1 rounded text-[#C53030] hover:bg-[#FFF5F5] transition-colors shrink-0"
                          title={t('rep.modal.geogrid.delete_query')}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Форма добавления нового запроса */}
              <form onSubmit={handleAddKeyword} className="space-y-1.5 pt-1">
                <div className="text-[11px] font-bold text-[#20231F]">
                  {t('rep.modal.geogrid.add_query_title')}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newKeywordInput}
                    onChange={(e) => setNewKeywordInput(e.target.value)}
                    placeholder={t('rep.modal.geogrid.placeholder')}
                    maxLength={200}
                    disabled={maxKeywords === null || count >= maxKeywords || saving}
                    className="flex-1 px-3 py-1.5 rounded-lg border border-[#E5E1D6] text-xs focus:outline-none focus:border-[#C8A653] disabled:bg-[#FAF9F5] disabled:cursor-not-allowed"
                  />
                  <button
                    type="submit"
                    disabled={!newKeywordInput.trim() || maxKeywords === null || count >= maxKeywords || saving}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#20231F] text-white font-semibold text-xs hover:bg-[#343A34] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>{t('rep.modal.geogrid.btn_add')}</span>
                  </button>
                </div>
              </form>

              {/* Предупреждение / Дисклеймер о расписании C2 */}
              <div className="p-3 rounded-lg bg-[#FFFBEB] border border-[#FDE68A] text-[#92400E] text-[11px] leading-relaxed flex items-start gap-2">
                <Calendar className="w-4 h-4 shrink-0 mt-0.5 text-[#D97706]" />
                <div>
                  <div className="font-bold">
                    {t('rep.modal.geogrid.schedule_title')}
                  </div>
                  <div>
                    {t('rep.modal.geogrid.schedule_desc')}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Подвал действий */}
        <div className="flex items-center justify-end gap-2 p-3 border-t border-[#E5E1D6] bg-[#FAF9F5]">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 rounded-lg border border-[#E5E1D6] bg-white text-[#20231F] font-semibold text-xs hover:bg-[#F1EEE4] transition-colors"
          >
            {t('rep.modal.geogrid.close')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading || maxKeywords === null || keywords.length === 0}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-[#0B8B5E] text-white font-semibold text-xs hover:bg-[#09734E] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-2xs"
          >
            {saving ? (
              <span>{t('rep.modal.geogrid.btn_saving')}</span>
            ) : (
              <>
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>{t('rep.modal.geogrid.btn_save')}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GeogridKeywordsModal;
